import { useCallback, useRef } from 'react';

import { type ActiveFiltersState } from '../../../shared/types';
import { useLazyGetFunctionAnalysisQuery } from '../../ViolationsTab/data-layer/violationsTabApi';
import { type FunctionAnalysisState } from '../../ViolationsTab/data-layer/violationsTabTypes';
import {
  useGetAssignedEntitlementsMutation,
  useGetViolatingEntitlementsMutation,
  useGetViolationFunctionMutation,
} from '../data-layer/detailsApi';
import {
  type ViolatingEntitlements,
  type ViolationAssignedEntitlements,
} from '../data-layer/detailsTypes';
import { getPayload } from '../utils';

const MAX_COUNT = 50;

const useConflictApiEndpoints = (
  activeFilters: ActiveFiltersState[],
  searchKey: string | null
) => {
  // Function API call
  const functionAbortRef = useRef<((reason: string) => void) | null>(null);
  const assignedEntAbortRef = useRef<((reason: string) => void) | null>(null);
  const violatingEntAbortRef = useRef<((reason: string) => void) | null>(null);

  const [
    fetchViolationFunctions,
    { isError: isFunctionError, isLoading: isFunctionLoading },
  ] = useGetViolationFunctionMutation();
  const [
    fetchAssignedEntitlements,
    {
      isError: isAssignedEntilementError,
      isLoading: isAssignedEntilementLoading,
    },
  ] = useGetAssignedEntitlementsMutation();
  const [
    fetchViolatingEntitlements,
    {
      isError: isViolatingEntilementError,
      isLoading: isViolatingEntilementLoading,
    },
  ] = useGetViolatingEntitlementsMutation();
  const [
    fetchFunctionAnalysis,
    { isError: isFunctionAnalysisError, isLoading: isFunctionAnalysisLoading },
  ] = useLazyGetFunctionAnalysisQuery();

  const handleFetchViolationFunctions = useCallback(
    async (violationId: string) => {
      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getPayload(activeFiltersMap, searchKey);

      functionAbortRef.current?.('New Search');

      try {
        const controller = fetchViolationFunctions({ violationId, payload });

        functionAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error("Failed violation functions' API");
      }
    },
    [fetchViolationFunctions, activeFilters, searchKey]
  );

  const handleFetchAssignedEntitlements = useCallback(
    async (
      violationId: string,
      functionId: string,
      parameterKey: string | null,
      config: { isOnExpand?: boolean; maxCount?: number | null } = {}
    ) => {
      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getPayload(activeFiltersMap, searchKey);

      if (!config.isOnExpand) {
        assignedEntAbortRef.current?.('New Search');
      }

      try {
        const controller = fetchAssignedEntitlements({
          violationId,
          functionId,
          payload: {
            ...payload,
            afterKey: parameterKey,
            max: config.maxCount ?? MAX_COUNT,
          },
        });

        assignedEntAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const { data, afterKey } = response as {
          data: ViolationAssignedEntitlements[];
          afterKey: string | null;
        };

        return {
          data,
          currentAfterKey: afterKey,
        };
      } catch {
        throw Error('Failed assigned entitlement API');
      }
    },
    [fetchAssignedEntitlements, activeFilters, searchKey]
  );

  const handleFetchViolatingEntitlements = useCallback(
    async (
      violationId: string,
      functionId: string,
      assignedEntitlementId: string,
      parameterKey: string[] | null
    ) => {
      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getPayload(activeFiltersMap, searchKey);

      violatingEntAbortRef.current?.('New Search');

      try {
        const controller = fetchViolatingEntitlements({
          violationId,
          functionId,
          assignedEntitlementId,
          payload: {
            ...payload,
            searchAfter: parameterKey,
            max: MAX_COUNT,
          },
        });

        violatingEntAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const { data, searchAfter } = response as {
          data: ViolatingEntitlements[];
          searchAfter: string[] | null;
        };

        return {
          data,
          currentAfterKey: searchAfter,
        };
      } catch {
        throw Error('Failed assigned entitlement API');
      }
    },
    [fetchViolatingEntitlements, activeFilters, searchKey]
  );

  const handleFetchFunctionAnalysis = useCallback(
    async (violationId: string) => {
      try {
        const response = await fetchFunctionAnalysis(violationId).unwrap();

        const { data } = response as {
          data: FunctionAnalysisState;
        };

        return { data };
      } catch {
        throw Error('Failed to get function analysis');
      }
    },
    [fetchFunctionAnalysis]
  );

  return {
    handleFetchViolationFunctions,
    handleFetchAssignedEntitlements,
    handleFetchViolatingEntitlements,
    handleFetchFunctionAnalysis,
    states: {
      isFunctionError,
      isFunctionLoading,
      isAssignedEntilementError,
      isAssignedEntilementLoading,
      isViolatingEntilementError,
      isViolatingEntilementLoading,
      isFunctionAnalysisError,
      isFunctionAnalysisLoading,
    },
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useConflictApiEndpoints };
